import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AdminScorecardBeyondNumbersPage = () => {
    const [settings, setSettings] = useState({
        tag_text: 'Beyond Numbers',
        title_text: 'How Aham Looks Beyond the Calculator',
        description_text: '',
        image_url: '',
        card_title: 'Doorstep Assessment',
        card_subtitle: 'Personalized income evaluation'
    });
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/eligibility-scorecard/beyond-numbers');
            if (response.data.success && response.data.data) {
                setSettings({
                    tag_text: response.data.data.tag_text || '',
                    title_text: response.data.data.title_text || '',
                    description_text: response.data.data.description_text || '',
                    image_url: response.data.data.image_url || '',
                    card_title: response.data.data.card_title || '',
                    card_subtitle: response.data.data.card_subtitle || ''
                });
            }
        } catch (error) {
            console.error('Error fetching Beyond Numbers data:', error);
            toast.error('Failed to load Beyond Numbers section data');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const data = new FormData();
            data.append('tag_text', settings.tag_text);
            data.append('title_text', settings.title_text);
            data.append('description_text', settings.description_text);
            data.append('card_title', settings.card_title);
            data.append('card_subtitle', settings.card_subtitle);

            if (selectedImage) {
                data.append('image', selectedImage);
            } else {
                data.append('image_url', settings.image_url);
            }

            const response = await axios.put('/api/admin/eligibility-scorecard/beyond-numbers', data, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                toast.success('Settings updated successfully');
                setSettings(prev => ({
                    ...prev,
                    image_url: response.data.data.image_url
                }));
                setSelectedImage(null);
                setImagePreview(null);
            }
        } catch (error) {
            console.error('Error saving Beyond Numbers settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#283079]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-4xl mx-auto">
            <div>
                <h1 className="text-3xl font-bold text-slate-800">Beyond Numbers Section</h1>
                <p className="text-slate-500 mt-1">Manage the "How Aham Looks Beyond the Calculator" section on the Scorecard page.</p>
            </div>

            <form onSubmit={handleSave} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Tag Text
                            </label>
                            <input
                                type="text"
                                value={settings.tag_text}
                                onChange={(e) => setSettings({ ...settings, tag_text: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                placeholder="e.g. Beyond Numbers"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Section Title
                            </label>
                            <input
                                type="text"
                                value={settings.title_text}
                                onChange={(e) => setSettings({ ...settings, title_text: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                placeholder="e.g. How Aham Looks Beyond the Calculator"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Description
                            </label>
                            <textarea
                                value={settings.description_text}
                                onChange={(e) => setSettings({ ...settings, description_text: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                rows="4"
                                placeholder="Enter description..."
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Card Title (Overlay card on image)
                            </label>
                            <input
                                type="text"
                                value={settings.card_title}
                                onChange={(e) => setSettings({ ...settings, card_title: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                placeholder="e.g. Doorstep Assessment"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Card Subtitle
                            </label>
                            <input
                                type="text"
                                value={settings.card_subtitle}
                                onChange={(e) => setSettings({ ...settings, card_subtitle: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                placeholder="e.g. Personalized income evaluation"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Section Image
                            </label>
                            <div className="flex flex-col gap-3">
                                {imagePreview || settings.image_url ? (
                                    <div className="relative rounded-2xl overflow-hidden aspect-[4/3] max-w-[240px] border border-slate-200">
                                        <img
                                            src={imagePreview || (settings.image_url.startsWith('http') ? settings.image_url : `/${settings.image_url}`)}
                                            alt="Beyond Numbers Image"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                ) : (
                                    <div className="rounded-2xl max-w-[240px] aspect-[4/3] bg-slate-50 border border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                                        No Image
                                    </div>
                                )}
                                <div className="space-y-1">
                                    <input
                                        type="file"
                                        accept=".png, .webp"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const allowedTypes = ['image/png', 'image/webp'];
                                                if (!allowedTypes.includes(file.type)) {
                                                    toast.error('Only PNG and WebP image formats are allowed!');
                                                    e.target.value = '';
                                                    return;
                                                }

                                                // Validate image dimensions
                                                const img = new Image();
                                                img.src = URL.createObjectURL(file);
                                                img.onload = () => {
                                                    if (img.width !== 1000 || img.height !== 750) {
                                                        toast.error(`Image must be exactly 1000 x 750 pixels. Got: ${img.width} x ${img.height} pixels.`);
                                                        e.target.value = '';
                                                        setSelectedImage(null);
                                                        setImagePreview(null);
                                                        return;
                                                    }
                                                    setSelectedImage(file);
                                                    setImagePreview(img.src);
                                                };
                                            }
                                        }}
                                        className="text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-slate-50 file:text-[#283079] hover:file:bg-slate-100"
                                    />
                                    <p className="text-xs text-slate-400 font-medium">Allowed formats: PNG, WebP only. Dimensions: exactly 900 x 600 px</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-100">
                    <button
                        type="submit"
                        disabled={saving}
                        className="bg-[#283079] hover:bg-[#1f2559] text-white px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                    >
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminScorecardBeyondNumbersPage;
